import React, {useEffect, useMemo, useRef, useState} from "react";
import {Bell, ChevronLeft, ChevronRight, Eye, Filter, HandCoins, Plus} from "lucide-react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    IconButton,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography
} from "@mui/material";
import {
    FATURA_STATUS_OPTIONS,
    formatCompetencia,
    formatDateBR,
    formatMoney,
    getFaturaStatusLabel,
    getFaturaStatusPalette
} from "./financeiroUtils";
import FaturaDetailsDialog from "./components/FaturaDetailsDialog";
import FaturaGenerateDialog from "./components/FaturaGenerateDialog";
import ActionConfirmationDialog from "./components/ActionConfirmationDialog";

const tableHeaderCellSx = {
    px: 3,
    py: 1.75,
    color: '#374151',
    fontSize: 14,
    fontWeight: 500,
    borderBottom: '1px solid #e5e7eb',
};

const tableBodyCellSx = {
    px: 3,
    py: 2,
    borderBottom: '1px solid #e5e7eb',
};

export default function FinanceiroFaturasPage({
    empresas,
    faturas,
    faturasPagination,
    faturasLoading,
    selectedFaturaDetails,
    detailsLoading,
    saving,
    statusFilter,
    page,
    onChangeStatusFilter,
    onChangePage,
    onOpenDetails,
    onCloseDetails,
    onCreateFatura,
    onReprocessInvoice,
    onNotifyCollection,
    onChanged,
    faturaParaAbrir,
}) {
    const [searchQuery, setSearchQuery] = useState('');
    const [generateOpen, setGenerateOpen] = useState(false);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [actionDialog, setActionDialog] = useState({open: false, type: null, payload: null});

    const filteredFaturas = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();

        return faturas.filter((fatura) => {
            const companyName = (fatura.empresa_nome || fatura.empresa?.nome || '').toLowerCase();
            const competencia = (fatura.competencia || '').toLowerCase();
            return !query || companyName.includes(query) || competencia.includes(query);
        });
    }, [faturas, searchQuery]);

    const openDetails = async (fatura) => {
        setDetailsOpen(true);
        await onOpenDetails?.(fatura.id);
    };

    // Vindo da tela de cobranças ("Abrir a fatura"): abre o detalhe uma vez só.
    const aberturaPedida = useRef(null);
    useEffect(() => {
        if (!faturaParaAbrir || aberturaPedida.current === faturaParaAbrir) return;
        aberturaPedida.current = faturaParaAbrir;
        setDetailsOpen(true);
        onOpenDetails?.(faturaParaAbrir);
    }, [faturaParaAbrir, onOpenDetails]);

    const closeDetails = () => {
        setDetailsOpen(false);
        onCloseDetails?.();
    };

    // O pagamento é de cada cobrança (BBF-158): o atalho abre a fatura, onde cada cobrança tem a ação.
    const canRegisterPayment = (fatura) => ['ABERTA', 'VENCIDA', 'PAGA_EM_PARTE'].includes(fatura?.status);

    const confirmAction = async () => {
        const {type, payload} = actionDialog;
        if (!payload || !type) return;

        if (type === 'notificar-cobranca') {
            await onNotifyCollection?.(payload.fatura_id || payload.id);
        }

        setActionDialog({open: false, type: null, payload: null});
    };

    return (
        <Stack spacing={2.5}>
            <Stack direction={{xs: 'column', sm: 'row'}} spacing={1.5}>
                <TextField
                    placeholder="Buscar por empresa ou competência..."
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    fullWidth
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            bgcolor: '#fff',
                        },
                    }}
                />
                <TextField
                    select
                    value={statusFilter || 'TODAS'}
                    onChange={(event) => {
                        onChangeStatusFilter?.(event.target.value === 'TODAS' ? '' : event.target.value);
                        onChangePage?.(1);
                    }}
                    sx={{
                        minWidth: {sm: 180},
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            bgcolor: '#fff',
                        },
                    }}
                >
                    <MenuItem value="TODAS">Todas</MenuItem>
                    {FATURA_STATUS_OPTIONS.map((status) => (
                        <MenuItem key={status} value={status}>{getFaturaStatusLabel(status)}</MenuItem>
                    ))}
                </TextField>
                <Button variant="outlined" startIcon={<Filter size={18} />} sx={{minWidth: 140, borderRadius: 2}}>
                    Filtros
                </Button>
                <Button
                    variant="contained"
                    startIcon={<Plus size={18} />}
                    onClick={() => setGenerateOpen(true)}
                    sx={{
                        borderRadius: 2,
                        boxShadow: 'none',
                        bgcolor: '#0f766e',
                        '&:hover': {bgcolor: '#115e59'},
                    }}
                >
                    Gerar Fatura
                </Button>
            </Stack>

            <Paper
                variant="outlined"
                sx={{
                    display: {xs: 'none', md: 'block'},
                    borderRadius: 2.5,
                    overflow: 'hidden',
                    borderColor: '#e5e7eb',
                    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
                }}
            >
                <Box sx={{overflowX: 'auto'}}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{bgcolor: '#f9fafb'}}>
                                <TableCell sx={tableHeaderCellSx}>Empresa</TableCell>
                                <TableCell sx={tableHeaderCellSx}>Competência</TableCell>
                                <TableCell sx={tableHeaderCellSx}>Valor Total</TableCell>
                                <TableCell sx={tableHeaderCellSx}>Status</TableCell>
                                <TableCell sx={tableHeaderCellSx}>Vencimento</TableCell>
                                <TableCell align="right" sx={tableHeaderCellSx}>Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {faturasLoading ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{py: 6}}>
                                        Carregando faturas...
                                    </TableCell>
                                </TableRow>
                            ) : !filteredFaturas.length ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{py: 6}}>
                                        Nenhuma fatura encontrada.
                                    </TableCell>
                                </TableRow>
                            ) : filteredFaturas.map((fatura) => {
                                const palette = getFaturaStatusPalette(fatura.status);
                                const isVencida = fatura.status === 'VENCIDA';

                                return (
                                    <TableRow key={fatura.id} hover sx={{bgcolor: isVencida ? '#fef2f2' : 'inherit'}}>
                                        <TableCell sx={tableBodyCellSx}>
                                            <Typography sx={{color: '#111827', fontWeight: 500}}>{fatura.empresa_nome || fatura.empresa?.nome || '-'}</Typography>
                                            <Typography variant="caption" color="text.secondary">{fatura.empresa_id || '-'}</Typography>
                                        </TableCell>
                                        <TableCell sx={tableBodyCellSx}>{formatCompetencia(fatura.competencia)}</TableCell>
                                        <TableCell sx={tableBodyCellSx}>{formatMoney(fatura.valor_total)}</TableCell>
                                        <TableCell sx={tableBodyCellSx}>
                                            <Chip
                                                size="small"
                                                label={getFaturaStatusLabel(fatura.status)}
                                                sx={{bgcolor: palette.bg, color: palette.color, border: `1px solid ${palette.border}`}}
                                            />
                                        </TableCell>
                                        <TableCell sx={tableBodyCellSx}>{formatDateBR(fatura.vencimento)}</TableCell>
                                        <TableCell align="right" sx={tableBodyCellSx}>
                                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                <IconButton size="small" onClick={() => openDetails(fatura)} sx={{color: '#0f766e'}}>
                                                    <Eye size={16} />
                                                </IconButton>
                                                {canRegisterPayment(fatura) ? (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => openDetails(fatura)}
                                                        aria-label="Registrar pagamento"
                                                        sx={{color: '#15803d'}}
                                                    >
                                                        <HandCoins size={16} />
                                                    </IconButton>
                                                ) : null}
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setActionDialog({open: true, type: 'notificar-cobranca', payload: fatura})}
                                                    sx={{color: '#2563eb'}}
                                                >
                                                    <Bell size={16} />
                                                </IconButton>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Box>
            </Paper>

            <Box sx={{display: {xs: 'grid', md: 'none'}, gap: 2}}>
                {faturasLoading ? (
                    <Paper variant="outlined" sx={{p: 3, borderRadius: 2.5, textAlign: 'center'}}>
                        <Typography color="text.secondary">Carregando faturas...</Typography>
                    </Paper>
                ) : !filteredFaturas.length ? (
                    <Paper variant="outlined" sx={{p: 3, borderRadius: 2.5, textAlign: 'center'}}>
                        <Typography color="text.secondary">Nenhuma fatura encontrada.</Typography>
                    </Paper>
                ) : filteredFaturas.map((fatura) => {
                    const palette = getFaturaStatusPalette(fatura.status);

                    return (
                        <Card key={fatura.id} variant="outlined" sx={{borderRadius: 2.5, borderColor: '#e5e7eb'}}>
                            <CardContent sx={{p: 2.25}}>
                                <Stack spacing={1.5}>
                                    <Stack direction="row" justifyContent="space-between" spacing={2}>
                                        <Box>
                                            <Typography sx={{color: '#111827', fontWeight: 600}}>{fatura.empresa_nome || fatura.empresa?.nome || '-'}</Typography>
                                            <Typography variant="body2" color="text.secondary">{formatCompetencia(fatura.competencia)}</Typography>
                                        </Box>
                                        <Chip
                                            size="small"
                                            label={getFaturaStatusLabel(fatura.status)}
                                            sx={{bgcolor: palette.bg, color: palette.color, border: `1px solid ${palette.border}`}}
                                        />
                                    </Stack>
                                    <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5}}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">Valor Total</Typography>
                                            <Typography>{formatMoney(fatura.valor_total)}</Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">Vencimento</Typography>
                                            <Typography>{formatDateBR(fatura.vencimento)}</Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">Fatura</Typography>
                                            <Typography>{fatura.id}</Typography>
                                        </Box>
                                    </Box>
                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                        <Button variant="outlined" size="small" onClick={() => openDetails(fatura)}>
                                            Ver detalhes
                                        </Button>
                                        {canRegisterPayment(fatura) ? (
                                            <Button
                                                variant="outlined"
                                                color="success"
                                                size="small"
                                                onClick={() => openDetails(fatura)}
                                            >
                                                Registrar pagamento
                                            </Button>
                                        ) : null}
                                        <Button
                                            variant="contained"
                                            size="small"
                                            onClick={() => setActionDialog({open: true, type: 'notificar-cobranca', payload: fatura})}
                                            sx={{bgcolor: '#0f766e', '&:hover': {bgcolor: '#115e59'}}}
                                        >
                                            Notificar
                                        </Button>
                                    </Stack>
                                </Stack>
                            </CardContent>
                        </Card>
                    );
                })}
            </Box>

            <Stack direction={{xs: 'column', sm: 'row'}} justifyContent="space-between" alignItems={{xs: 'flex-start', sm: 'center'}} spacing={2}>
                <Typography variant="body2" color="text.secondary">
                    Mostrando {faturasPagination.total ? ((faturasPagination.page - 1) * faturasPagination.page_size) + 1 : 0} a {Math.min((faturasPagination.page * faturasPagination.page_size), faturasPagination.total)} de {faturasPagination.total} registros
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                    <IconButton disabled={!faturasPagination.has_previous} onClick={() => onChangePage?.(Math.max(1, page - 1))}>
                        <ChevronLeft size={18} />
                    </IconButton>
                    <Typography variant="body2" color="text.secondary">
                        Página {faturasPagination.page} de {faturasPagination.total_pages}
                    </Typography>
                    <IconButton disabled={!faturasPagination.has_next} onClick={() => onChangePage?.(page + 1)}>
                        <ChevronRight size={18} />
                    </IconButton>
                </Stack>
            </Stack>

            <FaturaDetailsDialog
                open={detailsOpen}
                details={selectedFaturaDetails}
                loading={detailsLoading}
                onClose={closeDetails}
                onChanged={() => onChanged?.(selectedFaturaDetails?.fatura?.id)}
                onNotify={() => setActionDialog({open: true, type: 'notificar-cobranca', payload: selectedFaturaDetails?.fatura})}
                saving={saving}
                onReprocess={async () => {
                    const success = await onReprocessInvoice?.(selectedFaturaDetails?.fatura?.id);
                    if (success) {
                        closeDetails();
                    }
                    return success;
                }}
            />

            <FaturaGenerateDialog
                open={generateOpen}
                empresas={empresas}
                faturas={faturas}
                saving={saving}
                onClose={() => setGenerateOpen(false)}
                onSubmit={async (payload) => {
                    const success = await onCreateFatura(payload);
                    if (success) setGenerateOpen(false);
                }}
            />

            <ActionConfirmationDialog
                open={actionDialog.open}
                title="Enviar Notificação"
                message={`Enviar notificação de cobrança para "${actionDialog.payload?.empresa_nome || actionDialog.payload?.empresa?.nome}"?`}
                confirmLabel="Enviar"
                confirmColor="primary"
                onCancel={() => setActionDialog({open: false, type: null, payload: null})}
                onConfirm={confirmAction}
            />
        </Stack>
    );
}
